import { StageField } from '@n2-common/components/MultiStageOnePageWizard/types';
import { FormikCheckBox } from '@n2-common/ui/Input/CheckBox';
import { TFunction } from 'i18next';
import * as Yup from 'yup';

export type ConsentType = {
    MKT_1_PROFILING_1?: boolean;
    MKT_2_COOP_1?: boolean;
    MKT_3_COMM_1?: boolean;
    ConfirmSA?: boolean;
    MKT_3_COMM_1_kredittkort?: boolean;
    MKT_3_COMM_1_sparing?: boolean;
};

export function getConsent(name: keyof ConsentType, country: Country) {
    const CONSENTS = country == 'FI' ? CONSENTS_FI : country == 'NO' ? CONSENTS_NO : country == 'SE' ? CONSENTS_SE : [];

    const consent = CONSENTS.find((c) => c.name == name);
    if (!consent) {
        throw new Error(`Consent '${name}' for '${country}' was not found.`);
    }
    return consent;
}

export function getConsentFieldDef<TFormValues extends Record<string, unknown>>(
    name: keyof ConsentType,
    t: TFunction,
    country: Country,
    consentName: 'Consents' | 'consents' = 'Consents'
): StageField<TFormValues> {
    const consent = getConsent(name, country);
    return {
        name: `${consentName}.${name}` as StageField<TFormValues>['name'],
        Component: FormikCheckBox,
        componentProps: { label: consent.text, helptext: consent.popupBody ? consent.popupBody({}) : undefined },
        validation: consent.mandatory
            ? Yup.boolean().nullable().required(t<string>('validation:consent.required')).oneOf([true], t<string>('validation:consent.required'))
            : Yup.boolean().nullable(),
    };
}

export type ConsentDef = {
    name: keyof ConsentType | string;
    popupBody?: React.FC;
    text: string;
    mandatory: boolean;
    applicationFieldName?: string;
};

// Data grabbed from Sitecore in prod 2023/01/20
const CONSENTS_FI: ConsentDef[] = [
    {
        name: 'MKT_1_PROFILING_1',
        popupBody: () => (
            <div>
                Haluamme tarjota sinulle asiakkaana parasta mahdollista neuvontaa ja tarjouksia eri palveluistamme. Pankki k&auml;ytt&auml;&auml;
                t&auml;h&auml;n tarkoitukseen tietoja, joita pankilla on sinusta. T&auml;m&auml; sis&auml;lt&auml;&auml; esimerkiksi maksu- ja
                tilitapahtumahistorian sek&auml; pankin palveluiden, kuten verkkosivujemme k&auml;yt&ouml;n.
                <br />
                <br />
                Lue lis&auml;&auml; siit&auml;, kuinka pankki k&auml;sittelee henkil&ouml;kohtaisia tietojasi{' '}
                <a href="https://morrowbank.fi/cookies-yksityisyyspolitiikka">t&auml;&auml;ll&auml;</a>.<br />
                <br />
                Voit muuttaa suostumustietojasi Omilla Sivuilla.
                <br />
                <div>&nbsp;</div>
            </div>
        ),
        text: 'Haluan pankin ymmärtävän tarpeeni',
        mandatory: false,
    },
    {
        name: 'MKT_2_COOP_1',
        popupBody: () => (
            <div>
                Voidaksemme l&auml;hett&auml;&auml; asiakkaillemme yksil&ouml;ityj&auml; ja asiaankuuluvia tarjouksia, haluamme k&auml;ytt&auml;&auml;
                sek&auml; saada mahdollisuuden vaihtaa tietojasi kolmannen osapuolen kanssa. T&auml;m&auml; sis&auml;lt&auml;&auml; esimerkiksi maksu-
                ja k&auml;teisvarahistorian sek&auml; pankin palveluiden, kuten verkkosivujemme k&auml;yt&ouml;n. T&auml;ten voit vastaanottaa
                tarjouksia yhteisty&ouml;kumppaneidemme tuotteista. Listaus pankin tyt&auml;ryhti&ouml;ist&auml; l&ouml;ytyy{' '}
                <a href="https://morrowbank.fi/articles/yhteistyoekumppanimme">t&auml;&auml;lt&auml;</a>.&nbsp;
                <br />
                <br />
                Lue lis&auml;&auml; siit&auml;, kuinka pankki k&auml;sittelee henkil&ouml;kohtaisia tietojasi{' '}
                <a href="https://morrowbank.fi/cookies-yksityisyyspolitiikka">t&auml;&auml;ll&auml;</a>.<br />
                <br />
                Koska pankki on velvollinen informoimaan Morrow Bank luottokortti asiakkaita mahdollisuudesta tienata bonuspalkkioita, kortinhaltijat
                vastaanottavat suostumustiedoistaan huolimatta tarjouksia sellaisista yhteisty&ouml;kumppaneiden tuotteista, jotka kuuluvat
                kanta-asiakasohjelmaan.
                <br />
                <br />
                Voit muuttaa suostumustietojasi Omilla Sivuilla.
                <br />
                <div>&nbsp;</div>
            </div>
        ),
        text: 'Haluan vastaanottaa pankin yhteistyökumppaneilta yksilöityjä ja asiaankuuluvia tarjouksia',
        mandatory: false,
    },
    {
        name: 'MKT_3_COMM_1',
        applicationFieldName: 'AgreesCreditCheck',
        popupBody: () => null,
        text: `Vahvistan, että antamani tiedot ovat oikein ja hakemani luotto tulee omaan henkilökohtaiseen käyttööni. Hyväksyn luottotietojeni käytön sähköisesti ja hyväksyn, että kommunikointi tapahtuu elektronisesti esim. sähköpostitse ja puhelimitse ja että rekisteröityjä tietojani voidaan käyttää sähköiseen tiedon ja markkinointimateriaalin lähettämiseen Morrow Bankilta ja sen yhteistyökumppaneilta.`,
        mandatory: true,
    },
    {
        name: 'MKT_3_COMM_1_kredittkort',
        applicationFieldName: 'AgreesCreditCheck',
        popupBody: () => null,
        text: `Vahvistan, että antamani tiedot ovat oikein ja hakemani luotto tulee omaan henkilökohtaiseen käyttööni. Hyväksyn luottotietojeni käytön sähköisesti ja hyväksyn, että kommunikointi tapahtuu elektronisesti esim. sähköpostitse ja puhelimitse ja että rekisteröityjä tietojani voidaan käyttää sähköiseen tiedon ja markkinointimateriaalin lähettämiseen Morrow Bankilta ja sen yhteistyökumppaneilta.`,
        mandatory: true,
    },
    {
        name: 'MKT_3_COMM_1_sparing',
        applicationFieldName: 'AgreesCreditCheck',
        popupBody: () => null,
        text: `Vahvistan, että antamani tiedot ovat oikein ja hakemani luotto tulee omaan henkilökohtaiseen käyttööni. Hyväksyn luottotietojeni käytön sähköisesti ja hyväksyn, että kommunikointi tapahtuu elektronisesti esim. sähköpostitse ja puhelimitse ja että rekisteröityjä tietojani voidaan käyttää sähköiseen tiedon ja markkinointimateriaalin lähettämiseen Morrow Bankilta ja sen yhteistyökumppaneilta.`,
        mandatory: true,
    },
    {
        name: 'ConfirmSA',
        applicationFieldName: 'AgreesCreditCheck',
        popupBody: () => (
            <div>
                Annan suostumukseni sille, ett&auml; luotonantajat luovuttavat n&auml;it&auml; minua koskevia tietoja toisille luotonantajille.
                N&auml;it&auml; tietoja Morrow Bank ASA kysyy Suomen Asiakastieto Oy:n yll&auml;pit&auml;m&auml;n teknisen
                kyselyj&auml;rjestelm&auml;n avulla j&auml;rjestelm&auml;&auml;n osallistuvilta yhti&ouml;ilt&auml;.
                <br />
                <br />
                <span>
                    Kulutusluottojen kyselyj&auml;rjestelm&auml;st&auml; saatavia tietoja k&auml;sittelee ainoastaan Morrow Bank ASA ja Suomen
                    Asiakastieto Oy toimii tiedon v&auml;litt&auml;j&auml;n&auml;.
                </span>
                <br />
                <br />
                <span>K&auml;sitelt&auml;v&auml;t tiedot koskevat luottoja ja niiden hoitamista.</span>
                <br />
                <br />
                <span>
                    Tietoja ker&auml;t&auml;&auml;n vain luottohakemuksen k&auml;sittely&auml; varten. Luottohakemuksen yhteydess&auml;
                    ker&auml;ttyj&auml; tietoja ei luovuteta ulkopuolisille ilman lainmukaista velvoitetta. Viranomaisilla voi olla p&auml;&auml;sy
                    n&auml;ihin tietoihin omassa toiminnassaan.
                </span>
                <br />
                <br />
                <span>
                    Mik&auml;li luotto my&ouml;nnet&auml;&auml;n, tiedot s&auml;ilytet&auml;&auml;n viiden vuoden ajan siit&auml;, kun luotto on
                    kokonaisuudessaan er&auml;&auml;ntynyt maksettavaksi, ellei laissa s&auml;&auml;det&auml; velvollisuutta s&auml;ilytt&auml;&auml;
                    niit&auml; t&auml;t&auml; pidemp&auml;&auml;n. Mik&auml;li luottoa ei my&ouml;nnet&auml;, tiedot poistetaan kahden vuoden kuluttua
                    luottohakemuksen tekemisest&auml;.
                </span>
                <br />
                <br />
                <span>
                    Tiedot luovutetaan Suomen Asiakastieto Oy:n yll&auml;pit&auml;m&auml;n Kuluttajaluottojen kyselyj&auml;rjestelm&auml;n kautta.
                </span>
                <br />
                <br />
                <span>
                    Luotonhakijalla on oikeus tarkistaa mille tahoille ja mit&auml; tietoja Kuluttajaluottojen kyselyj&auml;rjestelm&auml;n kautta on
                    luovutettu. Tarkistusoikeuden toteuttaa Suomen Asiakastieto Oy.
                </span>
                <br />
                <br />
                <a href="-/media/51ebc30ec20542c5b94760080192be5d.ashx" target="_blank">
                    Lis&auml;tietoja Suomen Asiakastieto Oy:n yll&auml;pit&auml;m&auml;st&auml; Kuluttajaluottojen
                    kyselyj&auml;rjestelm&auml;st&auml;, sek&auml; j&auml;rjestelm&auml;n tietojen vaihtoon osallistuvat yhti&ouml;t l&ouml;yd&auml;t
                    t&auml;&auml;lt&auml;.&nbsp;
                </a>
            </div>
        ),
        text: `Annan toimeksiannon Morrow Bank ASA:lle kerätä ja käsitellä muilta luotonantajilta saatavia tietoja voimassa olevista luotoistani ja muista sitoumuksistani sisältäen tiedot, jotka olen antanut näille luotonantajille (kuten tiedot tuloistani) käytettäväksi luottohakemuksen käsittelyä varten.`,
        mandatory: true,
    },
];

const CONSENTS_NO: ConsentDef[] = [
    {
        name: 'MKT_1_PROFILING_1',
        popupBody: () => (
            <div>
                For &aring; kunne tilpasse v&aring;re r&aring;d og tilbud best mulig til deg som kunde, p&aring; tvers av produktkategorier
                &oslash;nsker banken &aring; benytte de opplysningene den har om deg. Dette omfatter eksempelvis transaksjons- og kontohistorikk og
                hvordan du benytter bankens tjenester, eksempelvis v&aring;re nettsider.
                <br />
                <br />
                Les mer om hvordan banken behandler personopplysninger{' '}
                <a rel="noopener noreferrer" href=": https://morrowbank.no/artikler/partnere" target="_blank">
                    her.
                </a>
                <br />
                <br />
                <span>Du kan endre samtykkeinnstillingene dine p&aring; Min Side etter at konto er opprettet.</span>
            </div>
        ),
        mandatory: false,
        text: 'Jeg ønsker at banken skal forstå hva jeg trenger',
        applicationFieldName: '',
    },
    {
        name: 'MKT_2_COOP_1',
        popupBody: () => (
            <div>
                For &aring; kunne sende deg relevante tilbud som er tilpasset deg som kunde, &oslash;nsker banken &aring; benytte og ha mulighet til
                &aring; utveksle de opplysningene den har om deg. Dette omfatter eksempelvis transaksjons- og beholdningshistorikk og hvordan du
                benytter bankens tjenester, eksempelvis v&aring;re nettsider. Du vil da kunne motta tilbud om v&aring;re samarbeidspartneres
                produkter. En oversikt over bankens samarbeidspartnere finner du{' '}
                <a href="~/link.aspx?_id=3A251CAB17FC49C19BF51A96C7486C3B&amp;_z=z" target="_blank">
                    her
                </a>
                .
                <br />
                <br />
                <p>Les mer om hvordan banken behandler personopplysninger&nbsp;</p>
                <a rel="noopener noreferrer" href="https://morrowbank.no/cookies-personvern" target="_blank">
                    her
                </a>
                .
                <br />
                <br />
                <p>
                    Siden banken plikter &aring; holde kunder av Morrow Bank kredittkort informert om opptjeningsmuligheter for bonus, vil kortkunder
                    uavhengig av dette samtykket motta tilbud om produkter fra de samarbeidspartnere som inng&aring;r i lojalitetsprogrammet.
                    <br />
                    <br />
                </p>
                <p>Du kan endre samtykkeinnstillingene dine p&aring; Min Side.</p>
            </div>
        ),
        mandatory: false,
        text: 'Jeg ønsker å motta relevante tilbud fra bankens samarbeidspartnere',
        applicationFieldName: '',
    },
    {
        name: 'MKT_3_COMM_1',
        applicationFieldName: 'AgreesCreditCheck',
        popupBody: () => null,
        text: `Ved å trykke "Send søknad" godtar jeg at det blir foretatt en kredittsjekk, samt et oppslag hos gjeldsinformasjonsforetak.`,
        mandatory: true,
    },
    {
        name: 'MKT_3_COMM_1_kredittkort',
        popupBody: () => null,
        mandatory: true,
        text: 'Jeg bekrefter at jeg søker på egne vegne og at opplysningene er riktige, samt godkjenner at kredittopplysninger innhentes og at all kommunikasjon skjer elektronisk.',
        applicationFieldName: 'AgreesCreditCheck',
    },
    {
        name: 'MKT_3_COMM_1_sparing',
        applicationFieldName: 'AgreesCreditCheck',
        popupBody: () => null,
        text: 'Jeg bekrefter at jeg søker på egne vegne og at opplysningene er riktige, samt godkjenner at kredittopplysninger innhentes og at all kommunikasjon skjer elektronisk.',
        mandatory: true,
    },
];
const CONSENTS_SE: ConsentDef[] = [
    {
        name: 'MKT_1_PROFILING_1',
        popupBody: () => (
            <div>
                F&ouml;r att kunna anpassa v&aring;ra r&aring;d och erbjudanden p&aring; b&auml;sta m&ouml;jliga s&auml;tt till dig som kund, i alla
                produktkategorier, &ouml;nskar banken anv&auml;nda de upplysningar den har om dig. Detta omfattar exempelvis transaktions- och
                kontohistorik och hur du anv&auml;nder bankens tj&auml;nster, exempelvis v&aring;ra hemsidor.&nbsp;
                <br />
                <br />
                L&auml;s mer om hur banken behandlar personuppgifter{' '}
                <a href="~/link.aspx?_id=C21C74E8E6994FF0AA227E8125DEA496&amp;_z=z" target="_blank">
                    h&auml;r
                </a>
                .<br />
                <br />
                Du kan &auml;ndra dina samtyckesinst&auml;llningar p&aring; Mina Sidor.
                <br />
                <div>&nbsp;</div>
            </div>
        ),
        mandatory: false,
        text: 'Jag önskar att banken ska förstå vad jag behöver',
        applicationFieldName: '',
    },
    {
        name: 'MKT_2_COOP_1',
        popupBody: () => (
            <div>
                F&ouml;r att kunna skicka relevanta och anpassade erbjudanden till dig som kund, &ouml;nskar banken att anv&auml;nda och ha
                m&ouml;jlighet till att utbyta upplysningarna den har om dig. Detta omfattar exempelvis transaktions- och kontohistorik och hur du
                anv&auml;nder bankens tj&auml;nster, exempelvis v&aring;ra hemsidor.&nbsp; Du kommer d&aring; kunna ta emot erbjudanden om v&aring;ra
                samarbetspartners produkter. En &ouml;versikt &ouml;ver bankens samarbetspartners finner du{' '}
                <a rel="noopener noreferrer" href="https://morrowbank.se/artiklar/partners" target="_blank">
                    h&auml;r
                </a>
                .&nbsp;&nbsp;
                <br />
                <br />
                L&auml;s mer om hur banken behandlar personuppgifter{' '}
                <a rel="noopener noreferrer" href="https://morrowbank.se/cookies-personuppgifter" target="_blank">
                    h&auml;r
                </a>
                .&nbsp; &nbsp;
                <br />
                <br />
                Du kan &auml;ndra dina samtyckesinst&auml;llningar p&aring; Mina Sidor.
                <br />
            </div>
        ),
        mandatory: false,
        text: 'Jag önskar ta emot relevanta erbjudanden från bankens samarbetspartners',
        applicationFieldName: '',
    },
    {
        name: 'MKT_3_COMM_1',
        applicationFieldName: 'AgreesCreditCheck',
        popupBody: () => null,
        text: `Jag/vi bekräftar att jag/vi ansöker för egen räkning och att informationen är korrekt, samt godkänner att kreditupplysningen inhämtas och att all kommunikation görs elektroniskt.`,
        mandatory: true,
    },
    {
        name: 'MKT_3_COMM_1_kredittkort',
        popupBody: () => null,
        mandatory: true,
        text: 'Jag/vi bekräftar att jag/vi ansöker för egen räkning och att informationen är korrekt, samt godkänner att kreditupplysningen inhämtas och att all kommunikation görs elektroniskt.',
        applicationFieldName: 'AgreesCreditCheck',
    },
    {
        name: 'MKT_3_COMM_1_sparing',
        applicationFieldName: 'AgreesCreditCheck',
        popupBody: () => null,
        text: `Jag/vi bekräftar att jag/vi ansöker för egen räkning och att informationen är korrekt, samt godkänner att all kommunikation görs elektroniskt.`,
        mandatory: true,
    },
];
